import React from 'react'
import PropTypes from 'prop-types'
import { View, StyleSheet } from 'react-native'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Button, Txt, TimeZoneSelect } from '../../../../components'
import InputFormik from '../../../../components/InputFormik'
import { usePlatforms } from '../../../../hooks/usePlatforms'
import { useIsDemo } from '../../../../hooks/useIsDemo'

export const setPayload = ({ email, phone_number, timezone }) => {
  const payload = {}
  if (email) {
    payload.email = email
  }
  if (phone_number) {
    payload.phone_number = phone_number
  }
  if (timezone && timezone.timezone_id) {
    payload.timezone_id = timezone.timezone_id
  }
  return payload
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .nullable(),
  phone_number: Yup.string()
    .matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid phone number')
    .nullable()
})

const CarrierInformationForm = ({ load, carrier, onSubmit }) => {
  const { isDesktop } = usePlatforms()
  const styles = useStyles(isDesktop)
  const isDemo = useIsDemo()

  const isFormUntouched = (values) => {
    return (values.email || '') === (carrier.email || '') && (values.phone_number || '') === (carrier.phone_number || '')
  }

  return (
    <View style={styles.container}>
      <Txt
        Key='carrierInformationTitle'
        text='Carrier Information'
        identity='subtitle'
        color='SLATE_GRAY'
        style={styles.title}
      />
      <Formik
        initialValues={carrier}
        onSubmit={values => onSubmit(setPayload(values))}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, handleSubmit, errors, touched }) => (
          <>
            <View style={styles.form}>
              <View style={styles.section}>
                <View style={[styles.input, isDesktop && styles.leftInput]}>
                  <InputFormik
                    name='name'
                    onChange={setFieldValue}
                    value={values?.name || ''}
                    placeholder='Company name'
                    letfIcon='User'
                    editable={false}
                    testID='profileCarrierCompanyNameInput'
                    accessibilityLabel='profileCarrierCompanyNameInput'
                  />
                </View>
                <View style={[styles.input, isDesktop && styles.rightInput]}>
                  <InputFormik
                    name='address1'
                    onChange={setFieldValue}
                    value={values?.address1}
                    placeholder='Address'
                    letfIcon='Location'
                    editable={false}
                    testID='profileCarrierAddressInput'
                    accessibilityLabel='profileCarrierAddressInput'
                  />
                </View>
              </View>
              <View style={styles.section}>
                <View style={[styles.input, isDesktop && styles.leftInput]}>
                  <InputFormik
                    name='email'
                    onChange={setFieldValue}
                    value={values?.email || ''}
                    placeholder='Email'
                    letfIcon='Email'
                    keyboardType='email-address'
                    error={touched.email && errors.email}
                    testID='profileCarrierEmailInput'
                    accessibilityLabel='profileCarrierEmailInput'
                    editable={!isDemo}
                  />
                </View>
                <View style={[styles.input, isDesktop && styles.rightInput]}>
                  <InputFormik
                    name='phone_number'
                    onChange={setFieldValue}
                    value={values?.phone_number || ''}
                    placeholder='Contact phone'
                    letfIcon='Phone'
                    error={touched.phone_number && errors.phone_number}
                    keyboardType='phone-pad'
                    testID='profileCarrierPhoneInput'
                    accessibilityLabel='profileCarrierPhoneInput'
                    editable={!isDemo}
                  />
                </View>
              </View>
              <View style={styles.input}>
                <TimeZoneSelect
                  name='timezone'
                  selected={values?.timezone || ''}
                  onChange={setFieldValue}
                  readonly
                />
              </View>
            </View>
            <View style={styles.button}>
              <Button
                onPress={handleSubmit}
                title='Save'
                loading={load}
                testID='profileCarrierSaveButton'
                accessibilityLabel='profileCarrierSaveButton'
                disabled={isDemo || isFormUntouched(values)}
              />
            </View>
          </>
        )}
      </Formik>
    </View>
  )
}

const useStyles = isDesktop => StyleSheet.create({
  container: {
    paddingHorizontal: 20,
    marginBottom: 20
  },
  form: {
  },
  title: {
    marginVertical: 20
  },
  input: {
    flex: 1
  },
  rightInput: {
    marginLeft: 5
  },
  leftInput: {
    marginRight: 5
  },
  section: {
    flexDirection: isDesktop ? 'row' : 'column',
    flex: 1
  },
  button: {
    alignItems: isDesktop ? 'flex-end' : 'stretch'
  }
})

CarrierInformationForm.propTypes = {
  load: PropTypes.bool.isRequired,
  carrier: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired
}

export default CarrierInformationForm
